import { Auth } from '../auth/Auth';
import axios, { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import { IMiddleware } from '../interface/IDoryConfig';

export class Ajax {
  //#region Fields
  private middleware: IMiddleware;

  private requestTimeout = 10000;
  /**
   * Returns request timeout (in ms)
   */
  public get RequestTimeout(): number {
    return this.requestTimeout;
  }
  public set RequestTimeout(newVal: number) {
    this.requestTimeout = newVal;
  }

  protected auth: Auth;
  //#endregion
  constructor(middleware: IMiddleware) {
    this.middleware = middleware;
  }
  /**
   * Use this function to override the HTTP GET Method
   *
   * @param request
   */
  private overrideRequestMethod(
    request: AxiosRequestConfig,
    method: Method
  ): void {
    const result = request.url.split(/(\?)(?!.*\?)/);

    request.url = result[0];
    request.data = result[result.length - 1];
    request.method = 'post';
    request.headers = {
      ...request.headers,
      ['Content-Type']: 'text/plain',
      ['X-HTTP-Method-Override']: method,
    };
  }

  /**
   * Use this function to make a 'native' Http-Request
   *
   * @typeParam T Type of the data field in request object
   *
   * @param request  Request object
   *
   * @returns The response of the server
   */
  public async request<T>(
    request: AxiosRequestConfig,
    auth?: Auth
  ): Promise<AxiosResponse<T>> {
    if (!request.headers) {
      request.headers = {};
    } else {
      request.headers = { ...request.headers };
    }
    // apply default timeout
    request.timeout = request.timeout ?? this.requestTimeout;
    // apply base url
    if (!request.url.startsWith('http')) {
      request.url = `${this.middleware.url}${request.url}`;
    }
    // apply default headers
    request.headers['Content-Type'] =
      request.headers['Content-Type'] ?? 'application/json';
    request.headers.Accept = request.headers.Accept ?? 'application/json';

    // if auth is provided send jwt header
    if (auth && request.headers.jwt === undefined) {
      request = await auth
        .validateToken()
        .catch((error) => Promise.reject(`Failed to validate token: ${error}`))
        .then(() => auth.getTokenString())
        .catch((error) =>
          Promise.reject(`Failed to get token string: ${error}`)
        )
        .then((jwt) => {
          request.headers.jwt = jwt;
          return request;
        });
    }

    // If the url is too long, we need to override the request method
    // this was introduce because of the IE11 limitation
    if (request.url.length > 2000) {
      if (request.method === 'get') {
        this.overrideRequestMethod(request, 'GET');
      }
      if (request.method === 'delete') {
        this.overrideRequestMethod(request, 'DELETE');
      }
    }

    switch (request.method) {
      case 'get': {
        return axios.get(request.url, request);
      }
      case 'post': {
        const data = request.data;
        delete request.data;
        return axios.post(request.url, data, request);
      }
      case 'put': {
        const data = request.data;
        delete request.data;
        return axios.put(request.url, data, request);
      }
      case 'delete': {
        return axios.delete(request.url, request);
      }
    }
  }

  /**
   * Perform a http get and receive a promise with the response of the middleware
   *
   * ```js example
   * // get REST data with Promises
   * let url = '/quote/info/?rics=BMWG.DE,VOWG.DE&fids=q.RIC,q.BID,q.ASK';
   *
   * dory.<module>.get(url)
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function (result) {
   *     console.log(result);
   *   });
   * ```
   * @category Rest
   *
   * @param url  The url of the endpoint
   * @param options  The axios request config options object
   *
   * @returns The response of the server
   */
  public get(
    url: string,
    options?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return this.request({ method: 'get', url, ...options }, this.auth);
  }

  /**
   * Perform a http put and receive a promise with the response of the middleware
   *
   * @category Rest
   *
   * @typeParam T Type of the data field in request object
   *
   * @param url  The url of the endpoint
   * @param data  The data object
   * @param options  The axios request config options object
   *
   * @returns The response of the server
   */
  public put<T>(
    url: string,
    data: T,
    options?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return this.request({ method: 'put', url, data, ...options }, this.auth);
  }

  /**
   * Perform a http post and receive a promise with the response of the middleware
   *
   * @category Rest
   *
   * @typeParam T The type of the data object
   *
   * @param url  The url of the endpoint
   * @param data  The data object
   * @param options  The axios request config options object
   *
   * @returns The response of the server
   */
  public post<T>(
    url: string,
    data: T,
    options?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return this.request({ method: 'post', url, data, ...options }, this.auth);
  }

  /**
   * Perform a http delete and receive a promise with the response of the middleware
   *
   * @category Rest
   *
   * @param url  The url of the endpoint
   * @param options  The axios request config options object
   *
   * @returns The response of the server
   */
  public delete(
    url: string,
    options?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return this.request({ method: 'delete', url, ...options }, this.auth);
  }
}
